import CONFIG_KEYS from "../config";
import Notifier from "../helper/notifier";

export type SseMessageType = "notification" | "message" | "heartbeat";

type Listener = (payload: any) => void;

/**
 * Single shared SSE connection for the whole app. Every push from the server
 * arrives as a plain "data:" line (no custom SSE "event:" name) with a "type"
 * field embedded in the JSON payload, so there's one onmessage handler here
 * that switches on that type and fans it out to whoever subscribed.
 */
class SseConnectionManagerClass {
    private es: EventSource | null = null;
    private connectedToken: string | null = null;
    private listeners: Record<SseMessageType, Listener[]> = {
        notification: [],
        message: [],
        heartbeat: [],
    };

    /** Opens the connection for this user if not already connected. Idempotent. */
    connect(userToken: string) {
        if (!userToken) return;
        if (this.es && this.connectedToken === userToken) return; // already connected

        this.disconnect();

        this.es = new EventSource(`${CONFIG_KEYS.sseUrl}/api/sse/stream?userToken=${userToken}`);
        this.connectedToken = userToken;

        this.es.onmessage = (e: MessageEvent) => {
            let data: any;
            try {
                data = JSON.parse(e.data);
            } catch {
                return;
            }

            switch (data.type as SseMessageType) {
                case "notification":
                    this.emit("notification", data);                    
                    break;
                case "message":
                    this.emit("message", data);
                    break;
                case "heartbeat": 
                    console.log("[SSE] Heartbeat");
                    this.emit("heartbeat", data);
                    break;
                default:
                    console.warn("Unknown SSE message type", data);
            }
        };

        this.es.onerror = () => {
            this.es?.close();
            this.es = null;
            this.connectedToken = null;
        };
    }

    disconnect() {
        this.es?.close();
        this.es = null;
        this.connectedToken = null;
    }

    /** Subscribe to one message type; returns an unsubscribe function. */
    on(type: SseMessageType, listener: Listener): () => void {
        this.listeners[type].push(listener);
        return () => {
            this.listeners[type] = this.listeners[type].filter(l => l !== listener);
        };
    }

    private emit(type: SseMessageType, payload: any) {
        this.listeners[type].forEach(l => l(payload));
    }
}

export const sseConnectionManager = new SseConnectionManagerClass();
